/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';

// @store
import { useSpinnerStore } from '../../../../store/useSpinnerStore';
import useServiceRequestStore from '../../../../store/useServiceRequestStore';
import { useCompanyStore } from '../../../../store/useCompanyStore';

// @types
import { ServiceRequest } from '../../../../types/request.types';

// @assets
import avatar from "../../../../assets/icons/avatar.svg"
import { formatLocalDate } from '../../../../helpers/date';

interface IRequestList {
    onSelectRequest: (request: ServiceRequest) => void;
}

const RequestList: React.FC<IRequestList> = ({ onSelectRequest }) => {
    const { t } = useTranslation();
    const { openSpinner } = useSpinnerStore();
    const { data } = useCompanyStore();
    const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
    const {
        getFilteredRequests,
        setFilters,
        filters,
        response,
        isLoading,
    } = useServiceRequestStore();

    useEffect(() => {
        if (isLoading) return openSpinner(true);
        return openSpinner(false);
    }, [isLoading])

    const getRequest = () => {
        if (!data?.category.categoryId) return;
        const params = {
            categoryId: data.category.categoryId,
            pageNumber: 1,
            pageSize: 100,
            applicatedByMe: false
        }
        getFilteredRequests(params);
    }

    useEffect(() => {
        getRequest();
    }, []);

    const selectRequest = (request: ServiceRequest) => {
        setSelectedRequestId(request.serviceRequestId);
        onSelectRequest(request);
    };

    return (
        <div className='w-full p-4 bg-cBackground dark:bg-dark-cBackground rounded-lg shadow-md h-[500px] xl:h-[75vh] overflow-auto'>
            <h3 className="font-semibold text-center text-cText text-[14px] mb-4 dark:text-dark-cText" >
                {t("professional.request.request")}
            </h3>

            {response?.items?.length
                ? response.items.map((request: ServiceRequest) => (
                    
                    <div key={request.serviceRequestId}
                        className={`flex flex-col lg:flex-row items-center justify-between gap-[10px] p-4 mb-[20px] rounded-lg shadow-md hover:shadow-lg cursor-pointer transition duration-300 ${selectedRequestId === request.serviceRequestId ? 'border border-bgBrand' : 'bg-cBackground dark:bg-dark-cBackground dark:border border-gray-600'}`}
                    >
                        {/* Foto y detalles */}
                        <div className="flex items-center gap-4">
                            <img
                                src={avatar}
                                alt="Avatar"
                                className="w-[40px] h-auto rounded-full object-cover border border-cBorder"
                            />
                            <div className="flex flex-col">
                                <p className="font-bold text-cText dark:text-dark-cText text-[14px] mb-1">
                                    {`${request.customer?.names || ""} ${request.customer?.lastNames || ""}`.trim()}
                                </p>
                                <p className="text-cTextSecondary dark:text-dark-cText/70 text-[12px]">
                                    {request.services[0]?.name || ""}
                                </p>
                                <p className="text-cTextSecondary dark:text-dark-cText/60 text-[12px]">
                                    {formatLocalDate(request?.createdAt)}
                                </p>
                            </div>
                        </div>

                        {/* Estado */}
                        <div className="flex flex-col items-end">
                            <button
                                className={`px-4 py-1 text-[12px] font-medium rounded-md ${selectedRequestId === request?.serviceRequestId ? 'bg-bgBrand text-textOnBrand' : 'bg-bgSuccess text-textOnSuccess'}`}
                                onClick={() => selectRequest(request)}
                            >
                                {t("professional.services.openService")}
                            </button>
                            {request.priority === 1 && (
                                <span className="mt-2 text-xs font-semibold text-bgError">
                                    {t("professional.request.urgent")}
                                </span>
                            )}
                        </div>
                    </div>
                ))
                : <div className="flex justify-center m-[10px] dark:text-dark-cText/60">
                    {t("professional.request.withoutRequest")}
                </div>

            }

            {response?.items?.length
                ? <div className='flex flex-col items-center justify-between gap-[10px] py-3 bg-white dark:bg-dark-cBackground border dark:border-gray-400 rounded-lg'>
                    <div className="flex items-center text-sm text-gray-500 onClick={() => dark:text-dark-cText/60">
                        <span>
                            {t("professional.request.paginationShow")}{" "}
                            <span className="font-medium">
                                {((filters.pageNumber || 1) - 1) * (filters.pageSize || 10) + 1}
                            </span>{" "}
                            {t("professional.request.paginationA")}{" "}
                            <span className="font-medium">
                                {Math.min(
                                    (filters.pageNumber || 1) * (filters.pageSize || 10),
                                    response.totalItems
                                )}
                            </span>{" "}
                            {t("professional.request.paginationOf")} <span className="font-medium">{response.totalItems}</span>{" "}
                            {t("professional.request.paginationResult")}
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setFilters({ pageNumber: (filters.pageNumber || 1) - 1 })}
                            disabled={filters.pageNumber === 1}
                            className={`px-4 py-2 text-sm font-medium rounded-md ${filters.pageNumber === 1
                                ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                                : "text-gray-700 bg-white hover:bg-gray-50 border"
                                }`}
                        >
                            {/* <span className="sr-only">{t("professional.request.previousPage")}</span> */}
                            {t("professional.request.previous")}
                        </button>

                        <span className="text-sm text-gray-500">
                            {t("professional.request.page")}{" "}
                            <span className="font-medium">{filters.pageNumber || 1}</span> {t("professional.request.paginationOf")}{" "}
                            <span className="font-medium">
                                {Math.ceil(response.totalItems / (filters.pageSize || 10))}
                            </span>
                        </span>

                        <button
                            onClick={() => setFilters({ pageNumber: (filters.pageNumber || 1) + 1 })}
                            disabled={(filters.pageNumber || 1) * (filters.pageSize || 10) >= response.totalItems}
                            className={`px-4 py-2 text-sm font-medium rounded-md ${(filters.pageNumber || 1) * (filters.pageSize || 10) >=
                                response.totalItems
                                ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                                : "text-gray-700 bg-white hover:bg-gray-50 border"
                                }`}
                        >
                            {/* <span className="sr-only">{t("professional.request.nextPage")}</span> */}
                            {t("professional.request.next")}
                        </button>
                    </div>
                </div>
                : null
            }

        </div>
    )
}

export default RequestList